import React, { useState, useEffect } from 'react';
import { fmt, api } from '../utils/api';

const FREQ_LABELS = {
  monthly: '/mo',
  weekly: '/wk',
  annual: '/yr',
};

export default function SubscriptionPanel({ data, onRefresh, onViewCharges }) {
  const [busyId, setBusyId] = useState(null);
  const [showIgnored, setShowIgnored] = useState(false);
  const [ignoredList, setIgnoredList] = useState(null);
  const stillChargingCount = ignoredList ? ignoredList.filter((i) => i.is_active && !i.charge_warning_dismissed).length : 0;
  const [loadingIgnored, setLoadingIgnored] = useState(false);
  const [dismissingAll, setDismissingAll] = useState(false);

  // Loaded eagerly (not just on expand) so the "still charging" warning
  // badge is visible even while the section is collapsed
  useEffect(() => {
    api.getIgnoredSubscriptions().then(setIgnoredList).catch(() => {});
  }, []);

  if (!data) return <PanelSkeleton />;

  const { subscriptions, monthlyTotal } = data;

  const markNotSubscription = async (id) => {
    setBusyId(id);
    try {
      await api.ignoreSubscription(id);
      onRefresh();
      if (ignoredList) loadIgnored(); // keep the ignored list in sync if it's open
    } catch (err) {
      console.error(err);
    } finally {
      setBusyId(null);
    }
  };

  const saveNickname = async (id, nickname) => {
    try {
      await api.setSubscriptionNickname(id, nickname);
      onRefresh();
    } catch (err) {
      console.error(err);
    }
  };

  const restore = async (id) => {
    setBusyId(id);
    try {
      await api.unignoreSubscription(id);
      onRefresh();
      loadIgnored();
    } catch (err) {
      console.error(err);
    } finally {
      setBusyId(null);
    }
  };

  const dismissWarning = async (id) => {
    setBusyId(id);
    try {
      await api.dismissChargeWarning(id);
      loadIgnored();
    } catch (err) {
      console.error(err);
    } finally {
      setBusyId(null);
    }
  };

  const dismissAll = async () => {
    setDismissingAll(true);
    try {
      await api.dismissAllChargeWarnings();
      loadIgnored();
    } catch (err) {
      console.error(err);
    } finally {
      setDismissingAll(false);
    }
  };

  const loadIgnored = async () => {
    setLoadingIgnored(true);
    try {
      const list = await api.getIgnoredSubscriptions();
      setIgnoredList(list);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingIgnored(false);
    }
  };

  const toggleIgnored = () => {
    const next = !showIgnored;
    setShowIgnored(next);
    if (next && !ignoredList) loadIgnored();
  };

  const grouped = {
    monthly: subscriptions.filter((s) => s.frequency === 'monthly'),
    annual: subscriptions.filter((s) => s.frequency === 'annual'),
    weekly: subscriptions.filter((s) => s.frequency === 'weekly'),
  };

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <h2 style={styles.title}>🔄 Subscriptions</h2>
        <div style={styles.totals}>
          <span style={styles.totalAmt}>{fmt.currency(monthlyTotal)}</span>
          <span style={styles.totalLabel}>/mo</span>
        </div>
      </div>

      {subscriptions.length === 0 ? (
        <p style={styles.empty}>
          No recurring charges detected yet. This needs at least two charges
          from the same merchant to identify a pattern — give it a billing
          cycle or two, or hit Sync after connecting new accounts.
        </p>
      ) : (
        Object.entries(grouped).map(([freq, subs]) => {
          if (subs.length === 0) return null;
          return (
            <div key={freq} style={styles.group}>
              <div style={styles.groupLabel}>
                {freq === 'monthly' && '📅 Monthly'}
                {freq === 'annual' && '📆 Annual'}
                {freq === 'weekly' && '🗓 Weekly'}
              </div>
              {subs.map((sub) => (
                <SubRow
                  key={sub.id}
                  sub={sub}
                  onNotSubscription={() => markNotSubscription(sub.id)}
                  onSetNickname={(nickname) => saveNickname(sub.id, nickname)}
                  onViewCharges={() => onViewCharges(sub.merchant_name)}
                  busy={busyId === sub.id}
                />
              ))}
            </div>
          );
        })
      )}

      <div style={styles.footer}>
        <span style={styles.footerLabel}>Annual recurring total</span>
        <span style={styles.footerAmt}>{fmt.currency(monthlyTotal * 12)}/yr</span>
      </div>

      <button style={styles.ignoredToggle} onClick={toggleIgnored}>
        {showIgnored ? '▾' : '▸'} Not Subscriptions
        {ignoredList ? ` (${ignoredList.length})` : ''}
        {stillChargingCount > 0 && (
          <span style={styles.stillChargingBadge}>⚠ {stillChargingCount} still charging</span>
        )}
      </button>

      {showIgnored && (
        <div style={styles.ignoredSection}>
          {loadingIgnored ? (
            <p style={styles.empty}>Loading...</p>
          ) : ignoredList && ignoredList.length > 0 ? (
            <>
              {stillChargingCount > 1 && (
                <button style={styles.dismissAllBtn} onClick={dismissAll} disabled={dismissingAll}>
                  {dismissingAll ? 'Dismissing...' : `✓ These are fine - dismiss all ${stillChargingCount} warnings`}
                </button>
              )}
              {[...ignoredList]
                .sort((a, b) => {
                  const aWarn = a.is_active && !a.charge_warning_dismissed;
                  const bWarn = b.is_active && !b.charge_warning_dismissed;
                  return (bWarn ? 1 : 0) - (aWarn ? 1 : 0);
                })
                .map((item) => {
                  const isWarning = item.is_active && !item.charge_warning_dismissed;
                  return (
                    <div key={item.id} style={{ ...styles.ignoredRow, ...(isWarning ? styles.ignoredRowWarning : {}) }}>
                      <div style={styles.ignoredInfo}>
                        <span style={styles.ignoredName}>{item.merchant_name}</span>
                        {isWarning ? (
                          <span style={styles.stillChargingNote}>
                            ⚠ Still charging {fmt.currency(item.last_amount)} · last on {fmt.shortDate(item.last_charged)} · {item.frequency}
                          </span>
                        ) : item.is_active && item.charge_warning_dismissed ? (
                          <span style={styles.dismissedNote}>Still charging, but dismissed - won't be flagged again</span>
                        ) : (
                          <span style={styles.stoppedNote}>No longer recurring</span>
                        )}
                      </div>
                      <div style={styles.ignoredActions}>
                        {isWarning && (
                          <button
                            style={styles.dismissBtn}
                            onClick={() => dismissWarning(item.id)}
                            disabled={busyId === item.id}
                            title="Confirm this isn't a subscription - stop warning me"
                          >
                            ✓ Fine
                          </button>
                        )}
                        <button
                          style={isWarning ? styles.restoreBtnWarning : styles.restoreBtn}
                          onClick={() => restore(item.id)}
                          disabled={busyId === item.id}
                        >
                          ↺ Restore
                        </button>
                      </div>
                    </div>
                  );
                })}
            </>
          ) : (
            <p style={styles.empty}>
              Nothing here. Mark a merchant "Not a subscription" above if
              recurring purchases (like groceries) get flagged by mistake —
              it'll show up here and can always be restored.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function SubRow({ sub, onNotSubscription, onSetNickname, onViewCharges, busy }) {
  const [editing, setEditing] = useState(false);
  const [nicknameInput, setNicknameInput] = useState(sub.nickname || '');

  const saveEdit = () => {
    onSetNickname(nicknameInput.trim());
    setEditing(false);
  };

  const freqPlain = { monthly: 'every month', weekly: 'every week', annual: 'every year' }[sub.frequency] || sub.frequency;

  return (
    <div>
    <div style={styles.subRow}>
      <div style={styles.subLeft}>
        {editing ? (
          <div style={styles.nicknameEditRow}>
            <input
              autoFocus
              style={styles.nicknameInput}
              placeholder={sub.merchant_name}
              value={nicknameInput}
              onChange={(e) => setNicknameInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
            />
            <button style={styles.nicknameSaveBtn} onClick={saveEdit}>Save</button>
            <button style={styles.nicknameCancelBtn} onClick={() => setEditing(false)}>✕</button>
          </div>
        ) : (
          <span style={styles.merchant} onClick={() => setEditing(true)} title="Click to rename">
            {sub.nickname || sub.merchant_name}
            {sub.nickname && <span style={styles.originalName}> ({sub.merchant_name})</span>}
            <span style={styles.editIcon}>✎</span>
          </span>
        )}
        {sub.potentially_canceled ? (
          <span style={styles.canceledBadge}>⚠ No charge in a while — canceled?</span>
        ) : sub.next_expected && (
          <span style={styles.nextDate}>
            Next: {fmt.shortDate(sub.next_expected)}
          </span>
        )}
      </div>
      <div style={styles.subRight}>
        <div style={styles.amtRow}>
          <span style={styles.subAmt}>
            {fmt.currency(sub.last_amount)}
            <span style={styles.freq}>{FREQ_LABELS[sub.frequency]}</span>
          </span>
          {sub.amount_changed && (
            <span style={{
              ...styles.changeBadge,
              color: sub.change_pct > 0 ? '#ef4444' : '#00d4aa',
              background: sub.change_pct > 0 ? 'rgba(239,68,68,0.1)' : 'rgba(0,212,170,0.1)',
            }}>
              {sub.change_pct > 0 ? '▲' : '▼'}
              {Math.abs(Math.round(sub.change_pct))}%
            </span>
          )}
        </div>
        <button
          style={styles.dismissBtn}
          onClick={onNotSubscription}
          disabled={busy}
          title="Not a subscription"
        >
          {busy ? '...' : sub.potentially_canceled ? 'Confirm canceled' : 'Not a subscription'}
        </button>
      </div>
    </div>
    <div style={styles.hintRow}>
      <span style={styles.hintText}>
        Recurring ~{freqPlain} · avg {fmt.currency(sub.avg_amount)} · last charged {fmt.shortDate(sub.last_charged)}
      </span>
      <button style={styles.viewChargesBtn} onClick={onViewCharges}>
        🔍 View charges →
      </button>
    </div>
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div style={styles.panel}>
      <div style={{ ...styles.skeleton, height: 24, width: '50%', marginBottom: 20 }} />
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} style={{ ...styles.skeleton, height: 52, marginBottom: 8 }} />
      ))}
    </div>
  );
}

const styles = {
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.25rem',
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  totals: { display: 'flex', alignItems: 'baseline', gap: '2px' },
  totalAmt: {
    fontSize: '1.2rem',
    fontWeight: '700',
    color: '#fbbf24',
    fontFamily: "'DM Mono', monospace",
  },
  totalLabel: { color: '#6b7280', fontSize: '0.8rem' },
  group: { marginBottom: '1rem' },
  groupLabel: {
    color: '#6b7280',
    fontSize: '0.7rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.4rem',
    fontFamily: "'Syne', sans-serif",
  },
  subRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.6rem 0.75rem',
    background: '#0a0f1e',
    borderRadius: '8px 8px 0 0',
    border: '1px solid #1e2a3a',
    borderBottom: 'none',
  },
  hintRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.4rem 0.75rem',
    background: '#0a0f1e',
    borderRadius: '0 0 8px 8px',
    border: '1px solid #1e2a3a',
    borderTop: '1px solid #1e2a3a',
    marginBottom: '0.35rem',
    flexWrap: 'wrap',
    gap: '0.4rem',
  },
  hintText: {
    color: '#4b5563',
    fontSize: '0.68rem',
    fontFamily: "'DM Mono', monospace",
  },
  viewChargesBtn: {
    background: 'transparent',
    border: 'none',
    color: '#00d4aa',
    fontSize: '0.68rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
  },
  subLeft: { display: 'flex', flexDirection: 'column', gap: '2px' },
  merchant: {
    color: '#e5e7eb',
    fontSize: '0.875rem',
    fontWeight: '500',
    fontFamily: "'Syne', sans-serif",
    cursor: 'pointer',
  },
  originalName: {
    color: '#4b5563',
    fontSize: '0.7rem',
    fontWeight: '400',
  },
  editIcon: {
    color: '#374151',
    fontSize: '0.7rem',
    marginLeft: '6px',
  },
  nicknameEditRow: { display: 'flex', gap: '0.4rem', alignItems: 'center' },
  nicknameInput: {
    background: '#0a0f1e',
    border: '1px solid #00d4aa',
    borderRadius: '6px',
    padding: '4px 8px',
    color: '#fff',
    fontSize: '0.8rem',
    fontFamily: "'Syne', sans-serif",
    width: '160px',
  },
  nicknameSaveBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '0.7rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  nicknameCancelBtn: {
    background: 'transparent',
    border: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    fontSize: '0.8rem',
  },
  nextDate: {
    color: '#4b5563',
    fontSize: '0.7rem',
    fontFamily: "'DM Mono', monospace",
  },
  canceledBadge: {
    color: '#fbbf24',
    fontSize: '0.7rem',
    fontFamily: "'Syne', sans-serif",
    fontWeight: '600',
  },
  subRight: { display: 'flex', alignItems: 'center', gap: '0.6rem' },
  amtRow: { display: 'flex', alignItems: 'center', gap: '0.35rem' },
  subAmt: {
    color: '#fff',
    fontSize: '0.9rem',
    fontWeight: '600',
    fontFamily: "'DM Mono', monospace",
  },
  freq: { color: '#6b7280', fontSize: '0.7rem' },
  changeBadge: {
    fontSize: '0.65rem',
    fontWeight: '700',
    padding: '2px 5px',
    borderRadius: '4px',
    fontFamily: "'DM Mono', monospace",
  },
  dismissBtn: {
    background: 'transparent',
    border: '1px solid #374151',
    color: '#6b7280',
    cursor: 'pointer',
    fontSize: '0.65rem',
    padding: '4px 8px',
    borderRadius: '6px',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '1px solid #1e2a3a',
  },
  footerLabel: { color: '#6b7280', fontSize: '0.8rem', fontFamily: "'Syne', sans-serif" },
  footerAmt: {
    color: '#fbbf24',
    fontSize: '0.9rem',
    fontWeight: '600',
    fontFamily: "'DM Mono', monospace",
  },
  empty: { color: '#4b5563', fontSize: '0.875rem', textAlign: 'center', padding: '1rem 0' },
  skeleton: {
    background: 'linear-gradient(90deg, #1e2a3a 25%, #263347 50%, #1e2a3a 75%)',
    borderRadius: '8px',
  },
  ignoredToggle: {
    background: 'transparent',
    border: 'none',
    color: '#4b5563',
    fontSize: '0.75rem',
    cursor: 'pointer',
    marginTop: '0.75rem',
    padding: 0,
    fontFamily: "'Syne', sans-serif",
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  stillChargingBadge: {
    background: 'rgba(239, 68, 68, 0.12)',
    color: '#ef4444',
    border: '1px solid #ef4444',
    borderRadius: '999px',
    padding: '2px 8px',
    fontSize: '0.65rem',
    fontWeight: '700',
  },
  ignoredSection: { marginTop: '0.5rem' },
  ignoredRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0.75rem',
    background: '#0a0f1e',
    borderRadius: '8px',
    marginBottom: '0.3rem',
    border: '1px solid #1e2a3a',
  },
  ignoredRowWarning: {
    background: 'rgba(239, 68, 68, 0.05)',
    borderColor: 'rgba(239, 68, 68, 0.35)',
  },
  ignoredInfo: { display: 'flex', flexDirection: 'column', gap: '2px' },
  ignoredName: {
    color: '#6b7280',
    fontSize: '0.8rem',
    fontFamily: "'Syne', sans-serif",
  },
  stillChargingNote: {
    color: '#ef4444',
    fontSize: '0.68rem',
    fontFamily: "'DM Mono', monospace",
  },
  stoppedNote: {
    color: '#374151',
    fontSize: '0.68rem',
    fontFamily: "'Syne', sans-serif",
  },
  restoreBtn: {
    background: 'transparent',
    color: '#00d4aa',
    border: '1px solid #00d4aa',
    borderRadius: '6px',
    padding: '3px 8px',
    fontSize: '0.65rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
  },
  restoreBtnWarning: {
    background: '#ef4444',
    color: '#fff',
    border: '1px solid #ef4444',
    borderRadius: '6px',
    padding: '3px 8px',
    fontSize: '0.65rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
  },
  ignoredActions: { display: 'flex', gap: '6px', flexShrink: 0 },
  dismissBtn: {
    background: 'transparent',
    color: '#00d4aa',
    border: '1px solid #00d4aa',
    borderRadius: '6px',
    padding: '3px 8px',
    fontSize: '0.65rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
  },
  dismissAllBtn: {
    width: '100%',
    background: 'rgba(0, 212, 170, 0.08)',
    color: '#00d4aa',
    border: '1px solid #00d4aa',
    borderRadius: '8px',
    padding: '0.55rem',
    fontSize: '0.72rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    marginBottom: '0.6rem',
  },
  dismissedNote: {
    color: '#4b5563',
    fontSize: '0.68rem',
    fontFamily: "'Syne', sans-serif",
    fontStyle: 'italic',
  },
};
